import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { badgeClass } from "@/lib/config";
import { AlertTriangle } from "lucide-react";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function ExpiringPolicies() {
  const nav = useNavigate();
  const [bucket, setBucket] = useState("30");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], summary: [], premium_at_risk: 0, total: 0 });

  const load = () => {
    const p = new URLSearchParams({ bucket });
    if (q) p.set("q", q);
    api.get(`/agency/expiring-policies?${p.toString()}`).then(({ data }) => setData(data)).catch(() => toast.error("Load failed"));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [bucket, q]);

  const cols = ["Client", "Policy #", "Carrier", "LOB", "Expiration", "Days", "Premium", "Producer", "Renewal", "Last Activity", "Next Follow-Up"];

  return (
    <div className="p-6" data-testid="expiring-policies-page">
      <PageHeader title="Expiring Policies" subtitle="Upcoming expirations with premium at risk — click through to the policy, client or renewal." />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {data.summary.map((s) => (
          <button key={s.key} data-testid={`bucket-${s.key}`} onClick={() => setBucket(s.key)}
            className={`text-left border rounded-sm p-3 nav-link ${bucket === s.key ? "bg-navy text-white border-navy" : "bg-white border-border hover:bg-ice"}`}>
            <div className={`text-[11px] uppercase ${bucket === s.key ? "text-white/70" : "text-mutedsea"}`}>{s.label}</div>
            <div className={`font-serif text-2xl ${bucket === s.key ? "text-white" : "text-navy"}`}>{s.count}</div>
            <div className={`text-xs ${bucket === s.key ? "text-white/80" : "text-mutedsea"}`}>{money(s.premium_at_risk)} at risk</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <Input data-testid="expiring-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search policy #, carrier, insured…" className="max-w-sm h-9" />
        <div className="ml-auto flex items-center gap-2 text-sm bg-seared/10 border border-seared/30 rounded-sm px-3 py-1.5 text-seared" data-testid="premium-at-risk">
          <AlertTriangle className="h-4 w-4" /> {money(data.premium_at_risk)} premium at risk · {data.total} policies
        </div>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{cols.map((c) => <th key={c} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold whitespace-nowrap">{c}</th>)}</tr></thead>
          <tbody data-testid="expiring-table">
            {data.items.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-mutedsea">No policies expiring in this window.</td></tr>}
            {data.items.map((r) => (
              <tr key={r.policy_id} data-testid={`expiring-row-${r.policy_id}`} className="border-b border-border last:border-0 hover:bg-ice">
                <td className="px-3 py-2"><button onClick={() => r.party_id && nav(`/clients/${r.party_id}`)} className="text-atlantic hover:underline nav-link">{r.client}</button></td>
                <td className="px-3 py-2"><button onClick={() => nav(`/policies/${r.policy_id}`)} className="text-atlantic hover:underline nav-link">{r.policy_number}</button></td>
                <td className="px-3 py-2 whitespace-nowrap">{r.carrier}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.line}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.expiration_date}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={r.days_remaining != null && r.days_remaining <= 30 ? "text-seared border-seared/40" : ""}>{r.days_remaining != null ? `${r.days_remaining}d` : "—"}</Badge></td>
                <td className="px-3 py-2 whitespace-nowrap">{money(r.premium)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.producer}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={badgeClass(r.renewal_status)}>{r.renewal_status}</Badge></td>
                <td className="px-3 py-2 whitespace-nowrap text-mutedsea">{r.last_activity || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-mutedsea">{r.next_follow_up || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
